import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type {
  B24ProjectOption,
  EmployeeSummary,
  PageResult,
  ProjectSummary,
  ProjectSyncReport,
} from '@blesk/contracts';
import { apiGet, apiPatch, apiPost } from '../../shared/api';
import { usePagedList } from '../../shared/lists';
import { PROJECT_STATUS_LABEL } from '../../shared/labels';
import { RU_TIMEZONES, formatDateInProjectTz, isKnownTimezone } from '../../shared/format';
import { useHasPermission, useSession } from '../../shared/session';
import { useUrlFilter } from '../../shared/filters';
import { PermissionGate } from '../shell/PermissionGate';
import { EmployeeCreateDialog, PasswordDialog } from '../shell/EmployeeDialogs';
import { ObserverPicker } from '../shell/ObserverPicker';
import {
  B24GroupLink,
  Dialog,
  EmptyRow,
  ErrorNote,
  Pager,
  RowMenu,
  orDash,
  useConfirm,
} from '../shell/ui';

/** §6.1, §13 ТЗ. Раздел «Объекты»: реестр объектов и их менеджеров. */
export function ProjectsPage() {
  return (
    <PermissionGate permission="projects.read">
      <ProjectsInner />
    </PermissionGate>
  );
}

type DialogState =
  | { kind: 'create' }
  | { kind: 'edit'; project: ProjectSummary }
  | { kind: 'manager'; project: ProjectSummary }
  | { kind: 'observers'; project: ProjectSummary }
  | { kind: 'fromBitrix' }
  | { kind: 'link'; project: ProjectSummary }
  | null;

/**
 * Действующие сотрудники для фильтра «Менеджер» и для диалога назначения.
 * Ключ кэша общий: список один и тот же, и открытие диалога не должно
 * перезапрашивать то, что уже показано в панели фильтров.
 */
function useEmployeeOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['employees', 'options'],
    queryFn: () =>
      apiGet<PageResult<EmployeeSummary>>('/api/employees', { status: 'ACTIVE', perPage: 200 }),
    select: (result) => result.items,
    enabled,
  });
}

function ProjectsInner() {
  const session = useSession();
  const canWrite = useHasPermission('projects.write');
  const confirmation = useConfirm();
  // Список сотрудников закрыт правом employees.read: без него фильтр
  // по менеджеру нечем наполнить, и запрос ушёл бы в 403
  const canPickManager = useHasPermission('employees.read');
  const queryClient = useQueryClient();

  const [status, setStatus] = useUrlFilter('status');
  const [managerId, setManagerId] = useUrlFilter('managerId');
  const [search, setSearch] = useUrlFilter('search');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [syncReport, setSyncReport] = useState<ProjectSyncReport | null>(null);

  const list = usePagedList<ProjectSummary>('projects', '/api/projects', {
    status,
    managerId,
    search,
  });
  const employees = useEmployeeOptions(canPickManager);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['projects'] });

  const managerOptions = useMemo(() => {
    const active = employees.data ?? [];
    const eligible = active
      .filter((employee) => employee.role === 'PROJECT_MANAGER')
      .map((employee) => ({ id: employee.id, fullName: employee.fullName }));
    if (!managerId || eligible.some((option) => option.id === managerId)) return eligible;

    // Отбор приходит ссылкой из «Пользователей и ролей» и может указывать на
    // сотрудника, который менеджером быть перестал. Без своей строки в списке
    // фильтр остался бы применённым, а поле — пустым: снять его нечем.
    const fullName =
      active.find((employee) => employee.id === managerId)?.fullName ??
      list.items.find((project) => project.manager?.id === managerId)?.manager?.fullName ??
      'Сотрудник вне списка';
    return [...eligible, { id: managerId, fullName }];
  }, [employees.data, list.items, managerId]);

  const archive = useMutation({
    mutationFn: (id: string) => apiPost(`/api/projects/${id}/archive`),
    onSuccess: invalidate,
  });
  const restore = useMutation({
    mutationFn: (id: string) => apiPost(`/api/projects/${id}/restore`),
    onSuccess: invalidate,
  });
  const sync = useMutation({
    mutationFn: () => apiPost<ProjectSyncReport>('/api/projects/sync'),
    onSuccess: (report) => {
      setSyncReport(report);
      invalidate();
    },
  });

  const integrated = session.mode !== 'STANDALONE';

  return (
    <>
      <h1>Объекты</h1>
      <p className="muted">
        Физического удаления нет: объект архивируется, приглашения при этом отзываются.
      </p>

      <div className="toolbar">
        <label>
          Статус
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Все</option>
            {Object.entries(PROJECT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {canPickManager && (
          <label>
            Менеджер
            <select value={managerId} onChange={(e) => setManagerId(e.target.value)}>
              <option value="">Все</option>
              {managerOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.fullName}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          Поиск
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Название или адрес"
          />
        </label>
        <span className="toolbar__spacer" />
        {canWrite && !integrated && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => setDialog({ kind: 'create' })}
          >
            Создать объект
          </button>
        )}
        {canWrite && integrated && (
          <>
            <button type="button" onClick={() => setDialog({ kind: 'fromBitrix' })}>
              Из проекта Bitrix24
            </button>
            <button type="button" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {sync.isPending ? 'Синхронизация…' : 'Синхронизировать'}
            </button>
          </>
        )}
      </div>
      <ErrorNote
        error={archive.error ?? restore.error ?? sync.error ?? list.error ?? employees.error}
      />

      {syncReport && (
        <p className="muted" role="status">
          Синхронизация: проверено {syncReport.checked}, переименовано {syncReport.renamed},
          менеджер изменён у {syncReport.managerChanged}
          {syncReport.needsDeactivation.length > 0 &&
            `, пропали из портала: ${syncReport.needsDeactivation.map((p) => p.name).join(', ')}`}
        </p>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Адрес</th>
              <th>Часовой пояс</th>
              <th>Статус</th>
              <th>Менеджер</th>
              <th>Администраторы</th>
              {/* В автономном режиме колонка была бы столбцом одинаковых
                  «автономный»: связи с порталом нет ни у одного объекта */}
              {integrated && <th>Bitrix24</th>}
              <th>Создан</th>
              {canWrite && <th>Действия</th>}
            </tr>
          </thead>
          <tbody>
            {list.items.length === 0 && (
              <EmptyRow
                colSpan={7 + (integrated ? 1 : 0) + (canWrite ? 1 : 0)}
                loading={list.isPending}
              />
            )}
            {list.items.map((project) => (
              <tr key={project.id}>
                <td>{project.name}</td>
                <td>{orDash(project.address)}</td>
                <td title={project.timezone}>{timezoneTitle(project.timezone)}</td>
                <td>{PROJECT_STATUS_LABEL[project.status]}</td>
                <td>
                  {orDash(project.manager?.fullName)}
                  {/* А13: наблюдатели задач Bitrix24 — под исполнителем, а не
                      отдельной колонкой: это свойство той же задачи */}
                  {integrated && project.observers.length > 0 && (
                    <div className="muted">
                      наблюдатели: {project.observers.map((o) => o.fullName).join(', ')}
                    </div>
                  )}
                </td>
                <td>
                  {/* Число само по себе — тупик: за ним стоит список персон,
                      и попасть в него отсюда должно быть одним нажатием */}
                  {project.activeAdmins === 0 ? (
                    0
                  ) : (
                    <Link to="/people" search={{ projectId: project.id }}>
                      {project.activeAdmins}
                    </Link>
                  )}
                </td>
                {integrated && (
                  <td>
                    <B24GroupLink groupId={project.bitrixGroupId} />
                  </td>
                )}
                <td>{formatDateInProjectTz(project.createdAt, project.timezone)}</td>
                {canWrite && (
                  <td>
                    <div className="row-actions">
                      <RowMenu
                        actions={
                          project.status === 'ARCHIVED'
                            ? [
                                {
                                  label: 'Восстановить',
                                  onSelect: () => restore.mutate(project.id),
                                },
                              ]
                            : [
                                {
                                  label: 'Изменить',
                                  onSelect: () => setDialog({ kind: 'edit', project }),
                                },
                                {
                                  label: project.manager
                                    ? 'Сменить менеджера'
                                    : 'Назначить менеджера',
                                  onSelect: () => setDialog({ kind: 'manager', project }),
                                },
                                // Наблюдатели существуют только как участники задачи
                                // Bitrix24, а сотрудников без employees.read не выбрать
                                ...(integrated && canPickManager
                                  ? [
                                      {
                                        label: 'Наблюдатели',
                                        onSelect: () => setDialog({ kind: 'observers', project }),
                                      },
                                    ]
                                  : []),
                                ...(integrated && project.bitrixGroupId === null
                                  ? [
                                      {
                                        label: 'Привязать к B24',
                                        onSelect: () => setDialog({ kind: 'link', project }),
                                      },
                                    ]
                                  : []),
                                {
                                  label: 'В архив',
                                  danger: true,
                                  onSelect: () =>
                                    confirmation.ask({
                                      title: 'Архивирование объекта',
                                      question: `Отправить «${project.name}» в архив?`,
                                      warning:
                                        'Действующие приглашения будут отозваны. Записи объекта сохраняются: это архив, а не удаление.',
                                      verb: 'В архив',
                                      danger: true,
                                      onConfirm: () => archive.mutate(project.id),
                                    }),
                                },
                              ]
                        }
                      />
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={list.page} perPage={list.perPage} total={list.total} onPage={list.setPage} />

      {dialog?.kind === 'create' && (
        <ProjectForm title="Новый объект" onClose={() => setDialog(null)} onDone={invalidate} />
      )}
      {dialog?.kind === 'edit' && (
        <ProjectForm
          title={`Изменение «${dialog.project.name}»`}
          project={dialog.project}
          onClose={() => setDialog(null)}
          onDone={invalidate}
        />
      )}
      {dialog?.kind === 'manager' && (
        <ManagerDialog
          project={dialog.project}
          onClose={() => setDialog(null)}
          onDone={invalidate}
        />
      )}
      {dialog?.kind === 'observers' && (
        <ObserversDialog
          project={dialog.project}
          onClose={() => setDialog(null)}
          onDone={invalidate}
        />
      )}
      {dialog?.kind === 'fromBitrix' && (
        <FromBitrixDialog onClose={() => setDialog(null)} onDone={invalidate} />
      )}
      {dialog?.kind === 'link' && (
        <LinkDialog project={dialog.project} onClose={() => setDialog(null)} onDone={invalidate} />
      )}
      {confirmation.dialog}
    </>
  );
}

/**
 * Зона в таблице называется так же, как в форме: одно и то же значение под
 * двумя разными именами читается как два разных. Идентификатор остаётся
 * в подсказке — он нужен при разборе расхождений со стороной заказчика.
 */
function timezoneTitle(timezone: string): string {
  return RU_TIMEZONES.find((item) => item.zone === timezone)?.title ?? timezone;
}

/**
 * Часовой пояс объекта. Свободный ввод здесь опасен: опечатка в IANA-идентификаторе
 * на экране не видна, а расписание считается уже в другой зоне (§9.2, §19).
 *
 * Зона, уже сохранённая у объекта, остаётся отдельной строкой списка: правка
 * названия не должна молча переносить объект в Москву. Нераспознанная зона
 * называется прямо — сервер её тоже отвергнет, и это лучше узнать до отправки.
 */
function TimezoneSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (timezone: string) => void;
}) {
  const listed = RU_TIMEZONES.some((item) => item.zone === value);
  return (
    <label>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} required>
        {RU_TIMEZONES.map((item) => (
          <option key={item.zone} value={item.zone}>
            {item.title}
          </option>
        ))}
        {!listed && value !== '' && (
          <option value={value}>
            {isKnownTimezone(value) ? value : `${value} — зона не распознана`}
          </option>
        )}
      </select>
    </label>
  );
}

/** Создание (сценарий 2) и правка объекта. Привязка к B24 — отдельная операция. */
function ProjectForm({
  title,
  project,
  onClose,
  onDone,
}: {
  title: string;
  project?: ProjectSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const session = useSession();
  const [name, setName] = useState(project?.name ?? '');
  const [address, setAddress] = useState(project?.address ?? '');
  // Новый объект получает зону контура по умолчанию (у заказчика — Калининград)
  const [timezone, setTimezone] = useState(project?.timezone ?? session.defaultTimezone);
  const [groupTag, setGroupTag] = useState(project?.groupTag ?? '');
  const [managerId, setManagerId] = useState('');
  const [addEmployee, setAddEmployee] = useState(false);
  const [issued, setIssued] = useState<{ employee: string; password: string } | null>(null);

  // Менеджер меняется отдельной операцией (§13), поэтому спрашиваем его только
  // при создании: в правке объекта это была бы вторая точка входа в то же действие
  const employees = useEmployeeOptions(!project);

  const save = useMutation({
    mutationFn: () => {
      if (project) {
        return apiPatch(`/api/projects/${project.id}`, {
          name,
          address: address || null,
          timezone,
          groupTag: groupTag || null,
        });
      }
      return apiPost('/api/projects', {
        name,
        ...(address ? { address } : {}),
        timezone,
        ...(groupTag ? { groupTag } : {}),
        ...(managerId ? { managerId } : {}),
      });
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog title={title} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          Название
          <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </label>
        <label>
          Адрес
          <input value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>
        <TimezoneSelect label="Часовой пояс" value={timezone} onChange={setTimezone} />
        <label>
          Тег группы для расписаний
          <input value={groupTag} onChange={(e) => setGroupTag(e.target.value)} />
        </label>
        {!project && (
          <ManagerField
            value={managerId}
            onChange={setManagerId}
            options={employees.data ?? []}
            onAddEmployee={() => setAddEmployee(true)}
          />
        )}
        <ErrorNote error={save.error ?? employees.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Сохранить
          </button>
        </div>
      </form>

      {addEmployee && (
        <EmployeeCreateDialog
          onClose={() => setAddEmployee(false)}
          onSaved={() => employees.refetch()}
          onCreated={(employee) => setManagerId(employee.id)}
          onPassword={setIssued}
        />
      )}
      {issued && (
        <PasswordDialog
          employee={issued.employee}
          password={issued.password}
          onClose={() => setIssued(null)}
        />
      )}
    </Dialog>
  );
}

/** E6: один активный менеджер; смена сохраняет снимки в прошлых событиях (сценарий 26). */
function ManagerDialog({
  project,
  onClose,
  onDone,
}: {
  project: ProjectSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [managerId, setManagerId] = useState(project.manager?.id ?? '');

  const employees = useEmployeeOptions(true);

  const save = useMutation({
    mutationFn: () =>
      apiPost(`/api/projects/${project.id}/manager`, { managerId: managerId || null }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog title={`Менеджер объекта «${project.name}»`} onClose={onClose}>
      <p className="muted">
        Прошлые инциденты и обращения сохранят прежнего ответственного — снимок не переписывается.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          Сотрудник
          <select value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            <option value="">Не назначен</option>
            {(employees.data ?? [])
              .filter((emp) => emp.role === 'PROJECT_MANAGER' || emp.id === project.manager?.id)
              .map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName}
                </option>
              ))}
          </select>
        </label>
        <ErrorNote error={save.error ?? employees.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Сохранить
          </button>
        </div>
      </form>

    </Dialog>
  );
}

/**
 * Дополнение №1, А13. Наблюдатели задач Bitrix24 по объекту — менеджер
 * коммерческого отдела. Исполнитель не меняется: он по-прежнему один.
 */
function ObserversDialog({
  project,
  onClose,
  onDone,
}: {
  project: ProjectSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [observerIds, setObserverIds] = useState(project.observers.map((o) => o.id));
  const employees = useEmployeeOptions(true);

  const save = useMutation({
    mutationFn: () => apiPost(`/api/projects/${project.id}/observers`, { observerIds }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  return (
    <Dialog title={`Наблюдатели «${project.name}»`} onClose={onClose}>
      <p className="muted">
        Наблюдатели видят задачи Bitrix24 по объекту и получают уведомления, но не отвечают за них:
        исполнитель — менеджер объекта. Руководитель производства и заместитель добавляются во все
        задачи настройкой в разделе «Правила негативной оценки». Прошлые задачи не меняются.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {employees.isPending ? (
          <p className="muted">Загрузка…</p>
        ) : (
          <ObserverPicker
            employees={employees.data ?? []}
            value={observerIds}
            onChange={setObserverIds}
            disabled={save.isPending}
          />
        )}
        <ErrorNote error={save.error ?? employees.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            Сохранить
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * §11.2, сценарий 1. Менеджер объекта — исполнитель будущих задач Bitrix24,
 * поэтому спрашивается сразу при создании, а не следующей операцией: объект,
 * заведённый без ответственного, упрётся в §11.3 на первом же инциденте.
 *
 * Список — действующие менеджеры объектов; рядом «Завести сотрудника» для
 * случая, когда нужного человека в системе ещё нет (обычно это владелец
 * проекта портала, §6.1).
 */
function ManagerField({
  value,
  onChange,
  options,
  hint,
  onAddEmployee,
}: {
  value: string;
  onChange: (id: string) => void;
  options: EmployeeSummary[];
  hint?: string;
  onAddEmployee?: () => void;
}) {
  return (
    <>
      <label>
        Менеджер объекта
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">— не назначен —</option>
          {options
            .filter((employee) => employee.role !== 'ANALYST')
            .map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName}
                {employee.bitrixUserId === null ? ' · без пользователя B24' : ''}
              </option>
            ))}
        </select>
      </label>
      {hint && <p className="muted">{hint}</p>}
      {/* Кнопка стоит рядом с меткой, а не внутри неё: клик по содержимому
          метки перебрасывается на её поле, и открытие окна уводило бы фокус */}
      {onAddEmployee && (
        <div>
          <button type="button" onClick={onAddEmployee}>
            Завести сотрудника
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Сценарий 1: объект из проекта портала. Название приходит из Bitrix24, а
 * менеджер — из владельца проекта (§6.1), если тот уже заведён сотрудником.
 * Не заведён — здесь же и заводится: возвращаться за этим в «Пользователей и
 * роли» значит создать объект без ответственного и забыть про него.
 */
function FromBitrixDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const session = useSession();
  const [bitrixGroupId, setBitrixGroupId] = useState('');
  const [timezone, setTimezone] = useState(session.defaultTimezone);
  const [managerId, setManagerId] = useState('');
  const [addEmployee, setAddEmployee] = useState(false);
  const [issued, setIssued] = useState<{ employee: string; password: string } | null>(null);

  const options = useQuery({
    queryKey: ['projects', 'bitrix-options'],
    queryFn: () => apiGet<B24ProjectOption[]>('/api/projects/bitrix/options'),
  });
  const employees = useEmployeeOptions(true);

  const save = useMutation({
    mutationFn: () =>
      apiPost('/api/projects/from-bitrix', {
        bitrixGroupId: Number(bitrixGroupId),
        timezone,
        ...(managerId ? { managerId } : {}),
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const free = (options.data ?? []).filter((option) => option.linkedProjectId === null);
  const selected = free.find((option) => String(option.bitrixGroupId) === bitrixGroupId) ?? null;

  /** Владелец проекта — кандидат по умолчанию; выбор человека его не переписывает. */
  const pickProject = (value: string) => {
    setBitrixGroupId(value);
    const option = free.find((item) => String(item.bitrixGroupId) === value);
    setManagerId(option?.ownerEmployeeId ?? '');
  };

  const ownerMissing = selected !== null && selected.ownerId !== null && !selected.ownerEmployeeId;

  return (
    <Dialog title="Объект из проекта Bitrix24" onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          Проект портала
          <select value={bitrixGroupId} onChange={(e) => pickProject(e.target.value)} required>
            <option value="">— выберите —</option>
            {free.map((option) => (
              <option key={option.bitrixGroupId} value={option.bitrixGroupId}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
        <TimezoneSelect label="Часовой пояс объекта" value={timezone} onChange={setTimezone} />
        <ManagerField
          value={managerId}
          onChange={setManagerId}
          options={employees.data ?? []}
          hint={
            ownerMissing
              ? `Владелец проекта в портале — ${selected?.ownerFullName ?? 'неизвестен'}; сотрудника с таким пользователем Bitrix24 нет.`
              : selected?.ownerEmployeeId
                ? 'Подставлен владелец проекта Bitrix24 (§6.1).'
                : undefined
          }
          onAddEmployee={() => setAddEmployee(true)}
        />
        <ErrorNote error={save.error ?? options.error ?? employees.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending || !bitrixGroupId}>
            Создать
          </button>
        </div>
      </form>

      {addEmployee && (
        <EmployeeCreateDialog
          onClose={() => setAddEmployee(false)}
          onSaved={() => employees.refetch()}
          onCreated={(employee) => setManagerId(employee.id)}
          onPassword={setIssued}
          prefill={
            ownerMissing
              ? {
                  fullName: selected?.ownerFullName ?? undefined,
                  bitrixUserId: selected?.ownerId ?? undefined,
                  role: 'PROJECT_MANAGER',
                }
              : undefined
          }
        />
      )}
      {issued && (
        <PasswordDialog
          employee={issued.employee}
          password={issued.password}
          onClose={() => setIssued(null)}
        />
      )}
    </Dialog>
  );
}

/** Привязка автономного объекта к группе портала. Отвязки не существует (§6.1). */
function LinkDialog({
  project,
  onClose,
  onDone,
}: {
  project: ProjectSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [bitrixGroupId, setBitrixGroupId] = useState('');

  const options = useQuery({
    queryKey: ['projects', 'bitrix-options'],
    queryFn: () => apiGet<B24ProjectOption[]>('/api/projects/bitrix/options'),
  });

  const save = useMutation({
    mutationFn: () =>
      apiPost(`/api/projects/${project.id}/link`, { bitrixGroupId: Number(bitrixGroupId) }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });

  const free = (options.data ?? []).filter((option) => option.linkedProjectId === null);

  return (
    <Dialog title={`Привязка «${project.name}» к Bitrix24`} onClose={onClose}>
      <p className="muted">Привязка необратима: идентификатор группы неизменяем.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <label>
          Проект портала
          <select value={bitrixGroupId} onChange={(e) => setBitrixGroupId(e.target.value)} required>
            <option value="">— выберите —</option>
            {free.map((option) => (
              <option key={option.bitrixGroupId} value={option.bitrixGroupId}>
                {option.name}
              </option>
            ))}
          </select>
        </label>
        <ErrorNote error={save.error ?? options.error} />
        <div className="form__actions">
          <button type="button" onClick={onClose}>
            Отмена
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending || !bitrixGroupId}>
            Привязать
          </button>
        </div>
      </form>
    </Dialog>
  );
}
